import {Alert, Box, Card, CardContent, CardHeader, Container, Grid, IconButton} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";
import {useAsync} from "react-async-hook";
import {useNavigate} from "react-router-dom";
import {DisplayUnitsCount} from "../../booking-utils";
import {useOrder} from "../../context/order";
import {useTenant} from "../../context/tenant";
import {B2Site} from "../../data/client.gen";
import {BoxAvailability, BoxPricing, useSiteApi} from "../../data/sites";
import {useLocalization} from "../../i18n";
import {DisplayCurrency, formatInteger, isBlank, notNull} from "../../utils";
import {OrderNav} from "./order-nav";

import Minus from "../../assets/minus-circle.svg";
import Plus from "../../assets/plus-circle.svg";

import SchluessfachLBox from "../../assets/schluessfach_l.png";
import SchluessfachMBox from "../../assets/schluessfach_m.png";

import VCXSShoppingBag from "../../assets/shopping-bag.jpg";
import VCLColor from "../../assets/VC-Boxsizes-L-large-transp-128evs-05k.png";
import VCXSLaptop from "../../assets/VC-Boxsizes-laptop-transp-128evs-05k.png";
import VCXSLaptop2 from "../../assets/VC-Boxsizes-laptop2-transp-128evs-05k.png";
import VCMColor from "../../assets/VC-Boxsizes-M-large-transp-128evs-05k.png";
import VCSColor from "../../assets/VC-Boxsizes-S-large-transp-128evs-05k.png";

import CityLockerStoreSki from "../../assets/citylockerstore_ski.jpg";

type VolumeIllustration = {
	text: string;
	trolleys?: number;
	suitcases?: number;
};

enum VisualisationStyle {
	Rendered = "Rendered",
	Schluessfach = "Schluessfach",
	CityLockerStore = "CityLockerStore",
}

interface Visualisation {
	boxType: string;
	src: string;
}

const renderedVisualisations: Visualisation[] = [
	{boxType: "XS", src: VCXSLaptop},
	{boxType: "S", src: VCSColor},
	{boxType: "M", src: VCMColor},
	{boxType: "L", src: VCLColor},
];

const cityLockerStoreVisualisations: Visualisation[] = [
	{boxType: "XS", src: VCXSLaptop2},
	{boxType: "S", src: VCXSShoppingBag},
	{boxType: "M", src: VCMColor},
	{boxType: "L", src: VCLColor},
	{boxType: "SKI", src: CityLockerStoreSki},
];

const schluessfachVisualisations: Visualisation[] = [
	{boxType: "M", src: SchluessfachMBox},
	{boxType: "L", src: SchluessfachLBox},
];

function typeToSize(type: string | undefined) {
	if (isBlank(type)) return 0;
	const sizes: Record<string, number> = {
		"XS": 100,
		"S": 200,
		"M": 300,
		"L": 400,
		"XL": 500,
		"XXL": 600,
		"SKI": 600,
	};
	return sizes[type as string] ?? 0;
}

function computeVisualisationStyle(tenantId: string | undefined) {
	switch (tenantId) {
		case "citylocker-store":
			return VisualisationStyle.CityLockerStore;
		case "schluessfach":
			return VisualisationStyle.Schluessfach;
		default:
			return VisualisationStyle.Rendered;
	}
}

function ImageBoxes({visualisationStyle, boxType, iconUrl}: {
	visualisationStyle: VisualisationStyle;
	boxType: string;
	iconUrl: string | undefined;
}) {
	let visualisations: Visualisation[];
	switch (visualisationStyle) {
		case VisualisationStyle.Schluessfach:
			visualisations = schluessfachVisualisations;
			break;
		case VisualisationStyle.CityLockerStore:
			visualisations = cityLockerStoreVisualisations;
			break;
		case VisualisationStyle.Rendered:
		default:
			visualisations = renderedVisualisations;
			break;
	}
	let imgSrc = `https://placehold.co/800x450?text=${boxType}`;
	const visualisation = visualisations.find((v) => v.boxType == boxType);
	if (visualisation) {
		imgSrc = visualisation.src;
	}
	if (iconUrl && iconUrl.length > 0) {
		if (iconUrl.indexOf("?X-Amz-Security-Token=") > 0) {
			iconUrl = iconUrl.split("?X-Amz-Security-Token=")[0];
		}
		imgSrc = iconUrl;
	}

	return (
		<Grid container justifyContent="center">
			<Grid item>
				<img style={{height: "auto", width: "100%", padding: "8px"}} alt={`Boxtype ${boxType}`} src={imgSrc} />
			</Grid>
		</Grid>
	);
}

interface OrderBoxesProps {
	site: B2Site;
}

export function OrderBoxes({site}: OrderBoxesProps) {
	const navigate = useNavigate();
	const {boxes = [], currency} = site;
	const {t, e} = useLocalization();
	const {tenant} = useTenant();
	const {getAvailability, getPricing} = useSiteApi();
	const {
		order: {dates: {from, until, fromDateTime, hours, unitCounts = 0}, boxes: {types = {}, boxesValid}},
		setBoxes,
	} = useOrder();

	useEffect(() => {
		if (tenant !== undefined && !from && !until && !fromDateTime && !hours) {
			navigate(`/${tenant.id}/${site.id}/dates`, {replace: true});
		}
	}, [tenant, site, from, until, fromDateTime, hours]);

	const {result: availability} = useAsync(async () =>
		tenant !== undefined
			? await getAvailability(tenant?.id, site.id, {from, until, fromDateTime, hours}) as Promise<BoxAvailability>
			: undefined, [tenant, site, from, until, fromDateTime, hours]);

	const {result: pricing} = useAsync(async () =>
		tenant !== undefined
			? await getPricing(tenant?.id, site.id, {from, until, fromDateTime, hours}) as Promise<BoxPricing>
			: undefined, [tenant, site, from, until, fromDateTime, hours]);

	useEffect(() => {
		if (pricing !== undefined) {
			setBoxes({
				types,
				boxesValid,
				pricing,
			});
		}
	}, [pricing]);

	const [clampedSizes, setClampedSizes] = useState<string[]>([]);

	// Re-validate the cart whenever availability (re)loads. A size selected while capacity was free can sell out while
	// the customer is deeper in the flow (pending holds release/expire minutes apart, so availability flaps). Without
	// this, a stale quantity survived in the order context and kept Next/submit enabled — the user only found out via
	// the generic submit error (#1202). Clamp any selection down to what is actually bookable now (0 → removed) and let
	// updateOrderBoxes recompute boxesValid, then surface a notice naming the affected sizes.
	useEffect(() => {
		if (availability === undefined) {
			return;
		}
		const reduced: string[] = [];
		const next: Record<string, number> = {...types};
		for (const [type, quantity] of Object.entries(types)) {
			// A type absent from the availability map is unconstrained (e.g. no capacity tracking) — leave it be.
			if (availability.hasOwnProperty(type) && quantity > availability[type]) {
				// Floor at 0 defensively: the backend clamps availability at 0 (#1244), but a negative quantity would
				// keep boxesValid truthy and submit a nonsense cart, so never trust the wire value blindly.
				next[type] = Math.max(0, availability[type]);
				reduced.push(type);
			}
		}
		if (reduced.length > 0) {
			setClampedSizes(reduced);
			updateOrderBoxes(next);
		}
	}, [availability]);

	const illustrations: Record<string, VolumeIllustration> = {
		XS: {
			trolleys: 1,
			suitcases: 0,
			text: t("boxes.labels.laptop_document"),
		},
		S: {
			trolleys: 2,
			suitcases: 1,
			text: t("boxes.labels.shopping_bags"),
		},
		M: {
			trolleys: 3,
			suitcases: 2,
			text: t("boxes.labels.trolleys_2_3"),
		},
		L: {
			trolleys: 4,
			suitcases: 3,
			text: t("boxes.labels.trolleys_3_4"),
		},
		XL: {
			trolleys: 5,
			suitcases: 4,
			text: t("boxes.labels.trolleys_3_4"),
		},
		XXL: {
			trolleys: 6,
			suitcases: 5,
			text: t("boxes.labels.trolleys_3_4"),
		},
		SKI: {
			text: t("boxes.labels.ski"),
		},
	};
	const visualisationStyle = useMemo(() =>
		tenant !== undefined
			? computeVisualisationStyle(tenant.id)
			: undefined, [tenant]);

	function updateOrderBoxes(types: Record<string, number>) {
		setBoxes({
			types,
			pricing,
			boxesValid: !!Object.values(types).find(_ => _),
		});
	}

	function handleMinusBox(type: string) {
		updateOrderBoxes({
			...types,
			[type]: Math.max(0, (types[type] ?? 0) - 1),
		});
	}

	function handlePlusBox(type: string) {
		const max: number = availability && availability[type] || 0;
		const quantity: number = (types[type] ?? 0) + 1;
		updateOrderBoxes({
			...types,
			[type]: max === 0 ? quantity : Math.min(max, quantity),
		});
	}

	return (
		<Container maxWidth="lg">
			{clampedSizes.length > 0 && (
				<Box mb={2}>
					<Alert
						severity="warning"
						onClose={() => setClampedSizes([])}
					>
						{t("boxes.soldOutNotice")}
					</Alert>
				</Box>
			)}
			<Grid
				container
				spacing={2}
				justifyContent="center"
			>
				{boxes
					.sort((a, b) => typeToSize(a.type) - typeToSize(b.type))
					.map(({type, ...box}) => ({
						type,
						quantity: (
							Object.entries(types)
								.map(([type, quantity]) => ({type, quantity}))
								.find(({type: boxType}) => boxType === type)
							|| {}
						)
							.quantity
							|| 0,
						max: availability && (!availability.hasOwnProperty(type) ? null : availability[type]),
						box,
					}))
					.map(({type, quantity, max, box: {width, height, depth, iconUrl, notes}}) => {
						const showTotalPrice = true; // if false will show price per unit as it was before Okt.2023
						let pricePerUnit = 0;
						let price = 0;
						if (pricing && pricing[type] && unitCounts > 0) {
							price = parseFloat((pricing[type]).toFixed(2));
							pricePerUnit = parseFloat((pricing[type] / unitCounts).toFixed(2));
						}

						return (
							<Grid
								key={type}
								item
								xs={12}
								sm={6}
								md={4}
							>
								<Card
									sx={theme => ({
										border: `1px solid ${theme.palette.primary.main}`,
									})}
								>
									<CardHeader
										title={
											<Grid
												container
												justifyContent="space-between"
												spacing={2}
											>
												<Grid
													item
													xs="auto"
												>
													<small>{t("boxes.type")}</small> <strong>{type}</strong>
												</Grid>
												<Grid
													item
													xs
												>
													<Box textAlign="right">
														{showTotalPrice
															? (
																<>
																	<small>{currency}</small>&nbsp;<DisplayCurrency
																		amount={price}
																	/>
																</>
															)
															: (
																<>
																	<small>{currency}</small>&nbsp;<DisplayCurrency
																		amount={pricePerUnit}
																	/>
																	<small>
																		/<DisplayUnitsCount
																			unit={site.bookingUnit}
																			count={1}
																		/>
																	</small>
																</>
															)}
													</Box>
												</Grid>
											</Grid>
										}
										sx={theme => ({
											color: theme.palette.primary.contrastText,
											backgroundColor: theme.palette.primary.main,
										})}
									/>
									<CardContent sx={{p: 0}}>
										{((illustrations[type] !== undefined || !isBlank(iconUrl))
											&& visualisationStyle) && (
											<Box
												px={1}
												py={2}
												minHeight={128}
												display="flex"
												sx={theme => ({
													boxSizing: "content-box",
													borderBottom: `1px solid ${theme.palette.primary.main}`,
												})}
											>
												<ImageBoxes
													visualisationStyle={visualisationStyle}
													boxType={type}
													iconUrl={iconUrl}
												/>
											</Box>
										)}
										<Box
											px={1}
											py={2}
											sx={theme => ({
												boxSizing: "content-box",
												borderBottom: `1px solid ${theme.palette.primary.main}`,
											})}
										>
											<Grid
												container
												justifyContent="space-around"
											>
												{Object.entries({width, height, depth}).map(([dimension, value]) => (
													<Grid
														key={dimension}
														item
													>
														<Box
															textAlign="center"
															color="primary"
														>
															<strong>{e("boxes.dimensions", dimension)}</strong>
															<br />
															<strong>
																{value !== undefined ? formatInteger(value / 10) : ""}
																{" "}
																cm
															</strong>
														</Box>
													</Grid>
												))}
											</Grid>
										</Box>
										{(illustrations[type] || !isBlank(notes)) && (
											<Box
												px={1}
												py={2}
												textAlign="center"
												sx={theme => ({
													boxSizing: "content-box",
													borderBottom: `1px solid ${theme.palette.primary.main}`,
												})}
											>
												{notes ? notes : illustrations[type].text}
											</Box>
										)}
										<Box
											px={1}
											py={2}
										>
											<Grid
												container
												alignItems="center"
												justifyContent="space-evenly"
											>
												<Grid item>
													<strong>{t("boxes.quantity")}:</strong>
												</Grid>
												<Grid item>
													<Grid
														container
														alignItems="center"
														justifyContent="center"
													>
														<Grid
															item
															xs="auto"
														>
															<IconButton
																disabled={quantity === 0}
																color="primary"
																onClick={() => handleMinusBox(type)}
															>
																<Minus style={{width: "1.6em"}} />
															</IconButton>
														</Grid>
														<Grid
															item
															xs="auto"
														>
															<Box
																minWidth="3em"
																textAlign="center"
																color="primary"
															>
																{max === 0
																	? <small>{t("boxes.notAvailable")}</small>
																	: (
																		<strong
																			style={{fontSize: "2em"}}
																		>
																			{quantity}
																		</strong>
																	)}
															</Box>
														</Grid>
														<Grid
															item
															xs="auto"
														>
															<IconButton
																disabled={notNull(max) && max <= quantity}
																color="primary"
																onClick={() => handlePlusBox(type)}
															>
																<Plus style={{width: "1.6em"}} />
															</IconButton>
														</Grid>
													</Grid>
												</Grid>
											</Grid>
										</Box>
									</CardContent>
								</Card>
							</Grid>
						);
					})}
			</Grid>
			<OrderNav
				site={site}
				onBack={() => navigate(`/${tenant?.id}/${site.id}/dates`)}
				onNext={() => navigate(`/${tenant?.id}/${site.id}/checkout`)}
				disabled={!boxesValid}
			/>
		</Container>
	);
}
